import 'react-circular-progressbar/dist/styles.css';

import { Avatar, Menu } from '@mui/material';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  buildStyles,
  CircularProgressbarWithChildren,
} from 'react-circular-progressbar';

import { Icons } from '../../../assets/SvgIcons';
import { ComponentHelper, useEffectAsync } from '../../../helper/Infra';
import {
  DependencyType,
  ICTask,
  ICTaskAttachment,
  ICTaskComment,
} from '../../../helper/Interfaces';
import { MergefloColors } from '../../../helper/MergefloColors';
import { getAssets } from '../../../store/feature/assetsSlice';
import { getPlayBookDetail } from '../../../store/feature/playBookDetailSlice';
import { selectedTaskDetailUpdate } from '../../../store/feature/taskSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import CustomCkEditor from '../CustomCkEditor/CustomCkEditor';
import CustomDropZone from '../CustomDropZone/CustomDropZone';
import classes from './DependencyCard.module.css';
import ProgressProvider from './ProgressProvider';
import { getPurePlaybookList } from '@Store/feature/purePlaybookSlice';

const componentHelper = new ComponentHelper();
type DependencyCardProps = {
  className?: string;
  dependencies?: ICTask;
  color?: string;
  taskDetail?: ICTask;
};

const DependencyCard: FC<DependencyCardProps> = ({
  className,
  dependencies: currentDependency,
  color,
  taskDetail,
}) => {
  const classesList = [classes.DependenciesCard, className];
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState<boolean>(false);
  const { functionalAreas } = useAppSelector(getAssets);
  const mergefloColors = new MergefloColors();
  const container = useRef<HTMLDivElement>(null);
  const purePlaybooks = useAppSelector(getPurePlaybookList);
  //const [visibleDetail, setVisibleDetail] = useState<boolean>(false);
  const [isCommentEditor, setIsCommentEditor] = useState<boolean>(false);
  const [taskComment, setTaskComment] = useState(
    currentDependency?.description ?? ''
  );
  const [commentList, setCommentList] = useState<ICTaskComment[]>([]); //Task Attachment
  const [attachments, setattachments] = useState<ICTaskAttachment[]>([]);
  const [description, setDescription] = useState(
    currentDependency?.description ?? ''
  );
  const [descriptionEdit, setDescriptionEdit] = useState<boolean>(false);

  const { userList } = useAppSelector(getPlayBookDetail);
  const currentUser = localStorage.getItem('username');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const visibleDetail = Boolean(anchorEl);
  const { dealTaskList } = useAppSelector(getPlayBookDetail);

  const [isDeleteDependencySource, setIsDeleteDependencySource] =
    useState<boolean>(
      purePlaybooks?.find(
        i => i.functionalAreaId === (currentDependency?.dependencySource ?? '')
      )?.project.isDeleted || false
    );
  const [isDeleteDependencyTarget, setIsDeleteDependencyTarget] =
    useState<boolean>(
      purePlaybooks?.find(
        i => i.functionalAreaId === (currentDependency?.dependencyTarget ?? '')
      )?.project.isDeleted || false
    );

  const handleClickOutside = (event: any) => {
    if (container.current && !container.current.contains(event.target)) {
      //setVisibleDetail(false);
    }
  };

  useEffectAsync(async () => {
    const taskAttachments = await componentHelper.getTaskAttachments(
      currentDependency?.id ?? ''
    );
    const currentDependencyTaskDetail = await componentHelper.getTaskDetail(
      currentDependency?.id ?? ''
    );
    const listComment = (await componentHelper.listTaskComments(
      currentDependency?.id ?? ''
    )) as ICTaskComment[];
    setDescription(currentDependencyTaskDetail.task?.description ?? '');
    setTaskComment('');
    setattachments(taskAttachments);
    setCommentList(listComment);
  }, [currentDependency]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const deleteAttachments = (attachment: ICTaskAttachment) => {
    setattachments(old => [...old.filter(item => item.id !== attachment.id)]);
  };

  const saveComments = async (inputComment: string) => {
    const elem = await componentHelper.createComment({
      comment: inputComment,
      taskId: currentDependency!.id!,
    });
    const createdComment = {
      comment: inputComment,
      taskId: currentDependency!.id!,
      id: elem.data.id,
      username: elem.data.createdBy,
      createdAt: elem.data.createdAt,
    } as ICTaskComment;
    setCommentList(old => [...old, createdComment]);
    setTaskComment('');
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const getPercentage = (task: DependencyType): any => {
    switch (task) {
      case DependencyType.subtask: {
        let tasks = dealTaskList.filter(
          i => i.parentId === currentDependency?.id && i.type === task
        );
        let tasksCompleted = dealTaskList.filter(
          i =>
            i.parentId === currentDependency?.id &&
            i.type === task &&
            i.isCompleted
        );

        if (tasks.length && tasksCompleted.length !== 0) {
          return Math.round(
            (Array.from(new Set(tasksCompleted.map(f => f.id))).length /
              Array.from(new Set(tasks.map(f => f.id))).length) *
              100
          );
        }
        return 0;
      }
      case DependencyType.risk: {
        let tasks = dealTaskList.filter(
          i => i.parentId === currentDependency?.id && i.type === task
        );
        let tasksCompleted = dealTaskList.filter(
          i =>
            i.parentId === currentDependency?.id &&
            i.type === task &&
            i.isCompleted
        );
        if (tasks.length && tasksCompleted.length !== 0) {
          return Math.round(
            (Array.from(new Set(tasksCompleted.map(f => f.id))).length /
              Array.from(new Set(tasks.map(f => f.id))).length) *
              100
          );
        }
        return 0;
      }

      case DependencyType.issue: {
        let tasks = dealTaskList.filter(
          i => i.parentId === currentDependency?.id && i.type === task
        );
        let tasksCompleted = dealTaskList.filter(
          i =>
            i.parentId === currentDependency?.id &&
            i.type === task &&
            i.isCompleted
        );
        if (tasks.length && tasksCompleted.length !== 0) {
          return Math.round(
            (Array.from(new Set(tasksCompleted.map(f => f.id))).length /
              Array.from(new Set(tasks.map(f => f.id))).length) *
              100
          );
        }
        return 0;
      }
      case DependencyType.decision: {
        let tasks = dealTaskList.filter(
          i => i.parentId === currentDependency?.id && i.type === task
        );
        let tasksCompleted = dealTaskList.filter(
          i =>
            i.parentId === currentDependency?.id &&
            i.type === task &&
            i.isCompleted
        );
        if (tasks.length && tasksCompleted.length !== 0) {
          return Math.round(
            (Array.from(new Set(tasksCompleted.map(f => f.id))).length /
              Array.from(new Set(tasks.map(f => f.id))).length) *
              100
          );
        }
        return 0;
      }

      default:
        break;
    }
  };

  const DependencyCardContentBGColor = () => {
    if (isDeleteDependencySource || isDeleteDependencyTarget) {
      return '#E0DFE6';
    }

    if (color) {
      return color;
    }

    if (
      functionalAreas?.find(
        i => i.id === currentDependency?.dependencyTarget
      ) &&
      functionalAreas?.find(i => i.id === currentDependency?.dependencyTarget)
        ?.color &&
      functionalAreas?.find(i => i.id === currentDependency?.dependencyTarget)
        ?.color !== ''
    ) {
      return functionalAreas?.find(
        i => i.id === currentDependency?.dependencyTarget
      )?.color;
    }

    return mergefloColors.getPlaybookColor(
      functionalAreas?.find(i => i.id === currentDependency?.dependencyTarget)
        ?.shortName ?? ''
    );
  };

  const IsDependencyIcon = () => {
    if (
      (currentDependency?.commentCount ?? 0) > 0 ||
      currentDependency?.hasDescription ||
      (currentDependency?.attachmentCount ?? 0) > 0
    ) {
      return <span className="vline"></span>;
    }
    return '';
  };

  return (
    <div
      style={{ position: 'relative', maxWidth: currentDependency ? 800 : 233 }}
      ref={container}
    >
      {currentDependency ? (
        <>
          <div
            className={classes.DependencyCardContent}
            style={{ cursor: 'pointer' }}
            onMouseEnter={handleClick}
            onClick={() => {
              if (currentDependency) {
                const data = {
                  selectedTask: currentDependency,
                  isShowTaskModal: true,
                };
                dispatch(selectedTaskDetailUpdate(data));
              }
            }}
          >
            <div
              className={classes.DependencyCardContentHeader}
              style={{
                backgroundColor: DependencyCardContentBGColor(),
                color:
                  isDeleteDependencySource || isDeleteDependencyTarget
                    ? '#DE350B'
                    : '#FFFFFF',
              }}
            >
              <span
                style={{
                  marginRight: 5,
                  // textDecoration: purePlaybooks?.find(
                  //   i =>
                  //     i.functionalAreaId ===
                  //     (currentDependency.dependencySource ?? '')
                  // )?.project.isDeleted
                  //   ? 'line-through'
                  //   : 'auto',
                }}
              >
                {
                  purePlaybooks?.find(
                    i =>
                      i.functionalAreaId ===
                      (currentDependency.dependencySource ?? '')
                  )?.project.taskPrefix
                }
                {`${isDeleteDependencySource ? ' (Delete)' : ''}`}
              </span>{' '}
              <Icons.ArrowDown
                color={
                  isDeleteDependencySource || isDeleteDependencyTarget
                    ? '#DE350B'
                    : '#FFFFFF'
                }
              />{' '}
              <span
                style={{
                  marginLeft: 5,
                  // textDecoration: purePlaybooks?.find(
                  //   i =>
                  //     i.functionalAreaId ===
                  //     (currentDependency.dependencyTarget ?? '')
                  // )?.project.isDeleted
                  //   ? 'line-through'
                  //   : 'auto',
                }}
              >
                {
                  purePlaybooks?.find(
                    i =>
                      i.functionalAreaId ===
                      (currentDependency.dependencyTarget ?? '')
                  )?.project.taskPrefix
                }
                {`${isDeleteDependencyTarget ? ' (Delete)' : ''}`}
              </span>
            </div>
            <div className={classes.DependencyCardContentFooter}>
              <div className="titledot" style={{ borderColor: 'green' }}></div>
              {currentDependency.dueDate
                ? moment(currentDependency.dueDate.split('T')[0]).format(
                    'MM-DD-YYYY'
                  )
                : 'No due date'}
              {IsDependencyIcon()}
              {(currentDependency?.commentCount ?? 0) > 0 && (
                <Icons.Comment color="#7D7A9A" />
              )}
              {currentDependency?.hasDescription && (
                <Icons.Description color="#7D7A9A" />
              )}
              {(currentDependency?.attachmentCount ?? 0) > 0 && (
                <Icons.Attachment color="#7D7A9A" width="15" height="15" />
              )}
            </div>
          </div>

          <div>
            <Menu
              id="demo-positioned-menu"
              aria-labelledby="demo-positioned-button"
              anchorEl={anchorEl}
              open={visibleDetail}
              onClose={handleClose}
              disableEnforceFocus
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              style={{ marginTop: 65 }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <div className={classes.dependencyDetail}>
                <div className={classes.dependencyDetailLeft}>
                  <span className="customAccordionTitle">Description</span>
                  {descriptionEdit ? (
                    <CustomCkEditor
                      mentions={userList}
                      height={150}
                      value={description}
                      onChange={evt => {
                        setDescription(evt);
                        const newTaskDetail = {
                          id: currentDependency?.id ?? '',
                          description: evt,
                        } as unknown as ICTask;

                        componentHelper.updateTask(newTaskDetail);
                      }}
                      setEditing={setDescriptionEdit}
                    />
                  ) : (
                    <div
                      onDoubleClick={() => setDescriptionEdit(true)}
                      dangerouslySetInnerHTML={{
                        __html:
                          description ||
                          'No Description. Double click to add description',
                      }}
                    />
                  )}

                  <div style={{ marginTop: 20 }}>
                    {' '}
                    <span className="customAccordionTitle">Files</span>
                  </div>
                  <CustomDropZone
                    taskAttachments={attachments}
                    setattachments={setattachments}
                    deleteAttachments={deleteAttachments}
                    taskId={currentDependency?.id ?? ''}
                  />
                  <div>
                    <span className="customAccordionTitle">Comments</span>

                    <div className="taskDetailCommentArea">
                      <Avatar
                        sx={{
                          bgcolor: mergefloColors.getUserColors(
                            currentUser?.charAt(0) ?? ''
                          ),
                          width: 34,
                          height: 34,
                          fontSize: 15,
                        }}
                      >
                        {currentUser !== null
                          ? currentUser.length
                            ? currentUser.includes(' ')
                              ? currentUser
                                  .split(' ')[0]
                                  .charAt(0)
                                  .toUpperCase() +
                                currentUser
                                  .split(' ')[1]
                                  .charAt(0)
                                  .toUpperCase()
                              : currentUser[0].toUpperCase()
                            : ''
                          : ''}
                      </Avatar>
                      {isCommentEditor ? (
                        <div
                          style={{
                            marginLeft: 20,
                            width: 450,
                          }}
                        >
                          <CustomCkEditor
                            //onBlur={() => setIsCommentEditor(false)}
                            value={taskComment}
                            onChange={(value: string) => {
                              setTaskComment(value);
                              saveComments(value);
                            }}
                            mentions={userList}
                            height={100}
                            setEditing={setIsCommentEditor}
                          />
                        </div>
                      ) : (
                        <div
                          className="taskDetailDescriptionInput"
                          onClick={() => {
                            setIsCommentEditor(true);
                          }}
                        >
                          Post your comments
                        </div>
                      )}
                    </div>
                    <div className="taskDetailPostButton">
                      {/*   <Button
                        className="FuchsiaBlueButton"
                        style={{ width: 126, margin: '14px 0px' }}
                        onClick={() => {
                          setIsCommentEditor(false);
                          saveComments();
                        }}
                      >
                        <span>Post Comment</span>
                      </Button> */}
                      {/* {isCommentEditor && (
                        <Button
                          style={{
                            marginLeft: 10,
                            textTransform: 'none',
                          }}
                          variant="text"
                          onClick={() => setIsCommentEditor(false)}
                        >
                          Cancel
                        </Button>
                      )} */}
                    </div>
                    <div
                      className="taskDetailCommentList"
                      style={{ display: 'flex', flexDirection: 'column' }}
                    >
                      {commentList
                        ?.sort((a, b) =>
                          (a as ICTaskComment).createdAt >
                          (b as ICTaskComment).createdAt
                            ? -1
                            : 1
                        )
                        .map(i => {
                          return (
                            <div
                              style={{ display: 'flex', padding: '5px 0' }}
                              key={i.id}
                            >
                              <div className="commentAvatar">
                                <Avatar
                                  sx={{
                                    bgcolor: mergefloColors.getUserColors(
                                      i.username.charAt(0) ?? ''
                                    ),
                                    width: 34,
                                    height: 34,
                                    fontSize: 15,
                                  }}
                                >
                                  {userList
                                    .find(user => user.username === i.username)
                                    ?.name?.charAt(0)
                                    ?.toUpperCase()}
                                  {userList
                                    .find(user => user.username === i.username)
                                    ?.surname?.charAt(0)
                                    ?.toUpperCase()}
                                </Avatar>
                              </div>
                              <div className="comment">
                                <div className="commentUser">
                                  {`${
                                    userList.find(
                                      user => user.username === i.username
                                    )?.name
                                  } ${
                                    userList.find(
                                      user => user.username === i.username
                                    )?.surname
                                  }`}{' '}
                                  -{' '}
                                  <small>
                                    {moment().format('MM/DD/YYYY') !==
                                    moment(i.createdAt).format('MM/DD/YYYY')
                                      ? moment(i.createdAt).format(
                                          'MM/DD/YYYY HH:mm'
                                        )
                                      : moment(i.createdAt).fromNow()}
                                  </small>
                                </div>
                                <div className="commentText">
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: i.comment,
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
                <div className={classes.dependencyDetailRight}>
                  <span className="customAccordionTitle">Task Details</span>
                  <div>
                    <div className={classes.date}>
                      <span>Start Date</span>
                      <span>
                        {currentDependency?.startDate
                          ? moment(
                              currentDependency?.startDate.split('T')[0]
                            ).format('MM/DD/YYYY')
                          : '-'}
                      </span>
                    </div>
                    <div className={classes.date}>
                      <span>End Date</span>
                      <span>
                        {currentDependency?.dueDate
                          ? moment(
                              currentDependency?.dueDate.split('T')[0]
                            ).format('MM/DD/YYYY')
                          : '-'}
                      </span>
                    </div>
                    <div className={classes.date}>
                      <span>Assignee</span>
                      <span>
                        {userList.find(
                          i => i.username === currentDependency?.assignee
                        )
                          ? userList.find(
                              i => i.username === currentDependency?.assignee
                            )?.name +
                            ' ' +
                            userList.find(
                              i => i.username === currentDependency?.assignee
                            )?.surname
                          : currentDependency.assignee ?? 'Unassigned'}
                      </span>
                    </div>
                    <div className={classes.date}>
                      <span>Subtasks</span>
                      <span style={{ width: 40 }}>
                        {' '}
                        {currentDependency?.subTaskCount !== 0 ? (
                          <ProgressProvider
                            valueStart={0}
                            valueEnd={getPercentage(DependencyType.subtask)}
                          >
                            {(value: number) => (
                              <CircularProgressbarWithChildren
                                value={value}
                                styles={buildStyles({
                                  pathColor: value < 35 ? `#d32f2f` : `#4caf50`,
                                })}
                              >
                                <span
                                  style={{
                                    fontFamily: 'Inter',
                                    fontSize: '11px',
                                    color: value < 35 ? `#d32f2f` : `#4caf50`,
                                  }}
                                >
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type ===
                                                DependencyType.subtask &&
                                              i.isCompleted
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                  /
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type === DependencyType.subtask
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                </span>
                              </CircularProgressbarWithChildren>
                            )}
                          </ProgressProvider>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>
                    <div className={classes.date}>
                      <span>Risks</span>
                      <span style={{ width: 40 }}>
                        {' '}
                        {currentDependency?.riskCount !== 0 ? (
                          <ProgressProvider
                            valueStart={0}
                            valueEnd={getPercentage(DependencyType.risk)}
                          >
                            {(value: number) => (
                              <CircularProgressbarWithChildren
                                value={value}
                                styles={buildStyles({
                                  pathColor: value < 35 ? `#d32f2f` : `#4caf50`,
                                })}
                              >
                                <span
                                  style={{
                                    fontFamily: 'Inter',
                                    fontSize: '11px',
                                    color: value < 35 ? `#d32f2f` : `#000`,
                                  }}
                                >
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type === DependencyType.risk &&
                                              i.isCompleted
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                  /
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type === DependencyType.risk
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                </span>
                              </CircularProgressbarWithChildren>
                            )}
                          </ProgressProvider>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>
                    <div className={classes.date}>
                      <span>Issues</span>
                      <span style={{ width: 40 }}>
                        {' '}
                        {currentDependency?.issueCount !== 0 ? (
                          <ProgressProvider
                            valueStart={0}
                            valueEnd={getPercentage(DependencyType.issue)}
                          >
                            {(value: number) => (
                              <CircularProgressbarWithChildren
                                value={value}
                                styles={buildStyles({
                                  pathColor: value < 35 ? `#d32f2f` : `#4caf50`,
                                })}
                              >
                                <span
                                  style={{
                                    fontFamily: 'Inter',
                                    fontSize: '11px',
                                    color: value < 35 ? `#d32f2f` : `#4caf50`,
                                  }}
                                >
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type === DependencyType.issue &&
                                              i.isCompleted
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                  /
                                  {
                                    Array.from(
                                      new Set(
                                        dealTaskList
                                          .filter(
                                            i =>
                                              i.parentId ===
                                                currentDependency?.id &&
                                              i.type === DependencyType.issue
                                          )
                                          .map(f => f.id)
                                      )
                                    ).length
                                  }
                                </span>
                              </CircularProgressbarWithChildren>
                            )}
                          </ProgressProvider>
                        ) : (
                          '-'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Menu>
          </div>
        </>
      ) : (
        <div onClick={() => setOpen(!open)} className={classesList.join(' ')}>
          <div>
            <div className={classes.newDependency}>
              <span>+ Add new dependency </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DependencyCard;
